import { useEffect } from "react";
import { Navigate, NavLink, Outlet, useNavigate, useOutletContext } from "react-router";

import { checkTabIsActive, IdValue, Spinner } from "@prequel/shared-components";

import type { DatalakeResource } from "@/api/import/datalakes";
import { useDatalake } from "@/api/import/datalakes";
import DetailPageLayout from "@/components/import/DetailPageLayout";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import { useToast } from "@/providers/ToastProvider";

export type DatalakeContext = {
  datalake: DatalakeResource;
};

export function useDatalakeContext(): DatalakeContext {
  return useOutletContext<DatalakeContext>();
}

const DatalakePage = () => {
  const datalakeID = useRequiredParam("datalakeID");
  const { addToast } = useToast();
  const { data: datalake, error, isLoading } = useDatalake(datalakeID);

  useEffect(() => {
    if (error) {
      addToast({ kind: "ERROR", message: "Failed to load datalake." });
    }
  }, [error, addToast]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!datalake) {
    return <Navigate to="/import/datalakes" replace />;
  }

  return <Outlet context={{ datalake }} />;
};

export const DatalakeDetailsPage = () => {
  const navigate = useNavigate();
  const { datalake } = useDatalakeContext();
  const basePath = `/import/datalakes/${datalake.id}`;

  const tabs = [
    <NavLink key="Overview" to={basePath} className={checkTabIsActive} end>
      Overview
    </NavLink>,
    <NavLink key="Providers" to={`${basePath}/providers`} className={checkTabIsActive} end>
      Providers
    </NavLink>,
    <NavLink key="Datasets" to={`${basePath}/datasets`} className={checkTabIsActive} end>
      Datasets
    </NavLink>,
    <NavLink key="Connection" to={`${basePath}/connection`} className={checkTabIsActive} end>
      Connection
    </NavLink>,
    <NavLink key="Admin" to={`${basePath}/admin`} className={checkTabIsActive} end>
      Admin
    </NavLink>,
  ];

  return (
    <DetailPageLayout
      title={datalake.name}
      onGoBack={() => navigate("/import/datalakes")}
      subheading={
        <div className="flex items-center">
          <IdValue id={datalake.id} />
        </div>
      }
      tabs={tabs}
    >
      <Outlet context={{ datalake }} />
    </DetailPageLayout>
  );
};

export default DatalakePage;
